import { Component, ElementRef, ViewChild, ViewChildren, QueryList, OnDestroy, HostListener, Input, Output, EventEmitter, OnInit, OnChanges, ChangeDetectorRef, NgZone } from '@angular/core';
import { Subscription } from 'rxjs';
import { VideoPlayerTVController } from 'src/app/_service/video-player-tv-controller.service';
import { TVNavigationService } from 'src/app/_service/tv-navigation.service';
import { DeviceService } from 'src/app/_service/tv-detect.service';
import { ScreenOrientation } from '@capacitor/screen-orientation';
import { Capacitor } from '@capacitor/core';
import { KeepAwake } from '@capacitor-community/keep-awake';
import { NavigationBar } from '@hugotomazi/capacitor-navigation-bar';
import { StatusBar, Style } from '@capacitor/status-bar';

declare let shaka: any;

export interface VideoTrack {
  id: string;
  language: string;
  label: string;
  type: 'audio' | 'text';
}

export interface QualityLevel {
  id: string;
  height: number;
  bandwidth: number;
  label: string;
}

@Component({
  selector: 'app-custom-video-player',
  templateUrl: './custom-video-player.component.html',
  styleUrls: ['./custom-video-player.component.css']
})
export class CustomVideoPlayerComponent implements OnInit, OnChanges, OnDestroy {
  @ViewChild('videoPlayer', { static: true }) videoElementRef!: ElementRef<HTMLVideoElement>;
  @ViewChild('videoContainer', { static: true }) videoContainerRef!: ElementRef<HTMLDivElement>;
  @ViewChild('seekBar', { static: true }) seekBarRef!: ElementRef<HTMLDivElement>;
  @ViewChild('volumeSlider', { static: true }) volumeSliderRef!: ElementRef<HTMLInputElement>;
  @ViewChildren('controlBtn') controlBtns!: QueryList<ElementRef>;
  @ViewChild('controlsContainer') controlsContainerRef!: ElementRef;

  @Input() mediaUrl: string = '';
  @Input() drmConfig: any = null;
  @Input() autoPlay: boolean = true;
  @Input() startTime: number = 0;
  @Input() title: string = '';
  @Input() showBackButton: boolean = true;
  @Input() hasEpisodes: boolean = false;
  @Input() hasMoreLikeThis: boolean = false;

  @Output() onPlay = new EventEmitter<void>();
  @Output() onPause = new EventEmitter<void>();
  @Output() onEnded = new EventEmitter<void>();
  @Output() onTimeUpdate = new EventEmitter<number>();
  @Output() onError = new EventEmitter<any>();
  @Output() onBackClick = new EventEmitter<void>();
  @Output() onMediaUrlChange = new EventEmitter<string>();
  @Output() onEpisodesToggle = new EventEmitter<boolean>();

  videoElement: HTMLVideoElement;
  player: any;
  
  isPlaying: boolean = false;
  isPaused: boolean = true;
  isLoading: boolean = true;
  isFullscreen: boolean = false;
  isMuted: boolean = false;
  isPictureInPicture: boolean = false;
  
  isMobile: boolean = false;
  isNativeMobile: boolean = false;
  
  currentTime: number = 0;
  duration: number = 0;
  bufferedTime: number = 0;
  volume: number = 1;
  playbackRate: number = 1;

  controlsVisible: boolean = true;
  showSettingsPanel: boolean = false;
  showQualityPanel: boolean = false;
  showAudioPanel: boolean = false;
  showCaptionsPanel: boolean = false;
  showSpeedPanel: boolean = false;

  availableQualities: QualityLevel[] = [];
  availableAudioTracks: VideoTrack[] = [];
  availableCaptions: VideoTrack[] = [];
  currentQuality: QualityLevel | null = null;
  currentAudioTrack: VideoTrack | null = null;
  currentCaption: VideoTrack | null = null;

  speedOptions = [0.25, 0.5, 0.75, 1, 1.25, 1.5, 1.75, 2];

  isSeeking: boolean = false;
  seekPreviewTime: number = 0;
  showSeekPreview: boolean = false;
  seekPreviewPosition: number = 0;
  isDraggingSeek: boolean = false;
  
  showEpisodesOverlay: boolean = false;

  lastTapTime: number = 0;
  tapCount: number = 0;
  touchStartX: number = 0;
  touchStartY: number = 0;
  mouseDownTime: number = 0;
  isMouseDown: boolean = false;
  isWaiting: boolean = false;

  // TV Navigation
  private tvFocusSub: Subscription | null = null;
  private tvInteractionSub: Subscription | null = null;
  private tvFocusedIndex: number = -1;
  private lastControlIndex: number = 0;

  private immersiveModeInterval: any = null;

  showSkipIndicator: boolean = false;
  skipIndicatorText: string = '';
  skipIndicatorSide: 'left' | 'right' | '' = '';
  private doubleTapThreshold: number = 300;

  private maintainImmersiveMode() {
    if (!this.isNativeMobile || this.isDestroyed) return;
    if (this.immersiveModeInterval) {
      this.clearScheduledInterval(this.immersiveModeInterval);
    }
    this.immersiveModeInterval = this.scheduleInterval(async () => {
      if (this.isDestroyed) return;
      try {
        await NavigationBar.hide();
        await StatusBar.hide();
        await StatusBar.setOverlaysWebView({ overlay: true });
      } catch (error) {
        console.warn('⚠️ Failed to maintain immersive mode:', error);
      }
    }, 5000);
  }

  private eventListeners: Array<() => void> = [];
  private timeouts: Set<any> = new Set();
  private intervals: Set<any> = new Set();
  private animationFrames: Set<number> = new Set();
  
  private controlsTimeout: any = null;

  private isInitialized: boolean = false;
  private isDestroyed: boolean = false;
  private playerReadyPromise: Promise<void> | null = null;

  constructor(
    private cdr: ChangeDetectorRef,
    private ngZone: NgZone,
    private tvController: VideoPlayerTVController,
    private navService: TVNavigationService,
    private deviceService: DeviceService
  ) {}

  ngOnInit() {
    this.detectMobile();
    this.initializePlayer();
    this.setupNativeFullscreen();
    if (this.deviceService.isTV()) {
      this.setupTVNavigation();
      // Suppress nav service handling — player handles all keys directly when controls hidden
      this.navService.suppressNavigation();
    }
  }

  ngOnChanges(changes: any) {
    if (changes.mediaUrl && !changes.mediaUrl.firstChange && this.mediaUrl) {
      this.loadNewMedia();
    }
    if (changes.startTime && !changes.startTime.firstChange && this.startTime > 0) {
      this.seek(this.startTime);
    }
  }

  ngOnDestroy() {
    this.isDestroyed = true;

    // TV cleanup
    if (this.tvFocusSub) {
      this.tvFocusSub.unsubscribe();
    }
    if (this.tvInteractionSub) {
      this.tvInteractionSub.unsubscribe();
    }
    if (this.deviceService.isTV()) {
      this.tvController.exitTrapMode();
      this.navService.resumeNavigation();
    }

    this.cleanup();
  }

  private detectMobile() {
    this.isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) || window.innerWidth <= 768 || ('ontouchstart' in window);
    this.isNativeMobile = Capacitor.isNativePlatform();
  }

  private isMobileBrowser(): boolean {
    return this.isMobile && !this.isNativeMobile;
  }

  private isLandscapeMode(): boolean {
    return window.innerWidth > window.innerHeight;
  }

  shouldShowControl(controlType: string): boolean {
    // Hide volume controls on TV (OS-controlled)
    if (this.deviceService.isTV() && controlType === 'volume') {
      return false;
    }

    const isLandscape = this.isLandscapeMode();
    if (!this.isMobile) {
      return true;
    }
    if (!isLandscape) {
      const portraitControls = ['play', 'settings', 'fullscreen'];
      return portraitControls.includes(controlType);
    }
    return true;
  }

  private async initializePlayer() {
    try {
      if (this.isDestroyed) return;
      this.playerReadyPromise = this.initPlayer();
      await this.playerReadyPromise;
      if (this.isDestroyed) return;
      this.setupControlsAutoHide();
      this.isInitialized = true;
      if (this.isNativeMobile) {
        await this.forceLandscapeMode();
      }
    } catch (error) {
      console.error('Failed to initialize player:', error);
      this.onError.emit(error);
    }
  }
  
  private async forceLandscapeMode() {
    if (this.isDestroyed || !this.isNativeMobile) return;
    try {
      await ScreenOrientation.lock({ orientation: 'landscape' });
      this.isFullscreen = true;
      this.cdr.detectChanges();
    } catch (error) {
      console.warn('⚠️ Native landscape mode failed:', error);
    }
  }

  private async lockLandscapeOrientation() {
    try {
      if (screen && (screen as any).orientation && (screen as any).orientation.lock) {
        try {
          await (screen as any).orientation.lock('landscape-primary');
          return;
        } catch (e1) {
          try {
            await (screen as any).orientation.lock('landscape');
            return;
          } catch (e2) {
            console.warn('Screen orientation lock failed');
          }
        }
      }
      if ((screen as any).lockOrientation) {
        (screen as any).lockOrientation('landscape-primary') || 
        (screen as any).lockOrientation('landscape');
      }
      if ((screen as any).webkitLockOrientation) {
        (screen as any).webkitLockOrientation('landscape-primary') || 
        (screen as any).webkitLockOrientation('landscape');
      }
    } catch (error) {
      console.warn('⚠️ All orientation lock methods failed:', error);
    }
  }
  
  private async setupNativeFullscreen() {
    if (!this.isNativeMobile) return;
    try {
      await ScreenOrientation.lock({ orientation: 'landscape' });
      await this.enableImmersiveMode();
      await KeepAwake.keepAwake();
    } catch (error) {
      console.warn('⚠️ Native fullscreen setup failed:', error);
    }
  }

  private async enableImmersiveMode() {
    if (!this.isNativeMobile) return;
    try {
      await StatusBar.setOverlaysWebView({ overlay: true });
      await StatusBar.hide();
      await NavigationBar.hide();
      if (StatusBar.setBackgroundColor) {
        await StatusBar.setBackgroundColor({ color: '#00000000' }); 
      }
      if (StatusBar.setStyle) {
        await StatusBar.setStyle({ style: Style.Dark });
      }
      this.maintainImmersiveMode();
    } catch (error) {
      console.warn('⚠️ Immersive mode setup failed:', error);
    }
  }

  private async disableImmersiveMode() {
    if (!this.isNativeMobile) return;
    try {
      if (this.immersiveModeInterval) {
        this.clearScheduledInterval(this.immersiveModeInterval);
        this.immersiveModeInterval = null;
      }
      await StatusBar.setOverlaysWebView({ overlay: false });
      await NavigationBar.show();
      await StatusBar.show();
      await KeepAwake.allowSleep();
    } catch (error) {
      console.warn('⚠️ Immersive mode disable failed:', error);
    }
  }

  private async enterBrowserFullscreen() {
    if (this.isDestroyed) return;
    try {
      const element = this.videoContainerRef.nativeElement;
      if (element.requestFullscreen) {
        await element.requestFullscreen();
      } else if ((element as any).webkitRequestFullscreen) {
        await (element as any).webkitRequestFullscreen();
      } else if ((element as any).msRequestFullscreen) {
        await (element as any).msRequestFullscreen();
      } else if ((element as any).mozRequestFullScreen) {
        await (element as any).mozRequestFullScreen();
      }
    } catch (error) {
      console.warn('❌ Browser fullscreen failed:', error);
    }
  }

  private async initPlayer() {
    if (this.isDestroyed) return;
    try {
      if (!shaka || !shaka.Player) {
        throw new Error('Shaka Player not loaded');
      }
      shaka.polyfill.installAll();
      if (!shaka.Player.isBrowserSupported()) {
        throw new Error('Shaka Player is not supported in this browser.');
      }
      this.videoElement = this.videoElementRef.nativeElement;
      if (this.player) {
        await this.player.destroy();
        this.player = null;
      }
      this.player = new shaka.Player(this.videoElement);
      if (this.drmConfig) {
        this.player.configure({
          textDisplayFactory: new shaka.text.SimpleTextDisplayer(this.videoElement),
          drm: this.drmConfig,
          streaming: {
            bufferingGoal: 30,
            rebufferingGoal: 5,
            bufferBehind: 30
          }
        });
      }
      this.setupEventListeners();
      if (this.mediaUrl) {
        await this.loadMedia();
      }
    } catch (error) {
      console.error('❌ Player initialization failed:', error);
      throw error;
    }
  }

  private setupEventListeners() {
    if (!this.videoElement || this.isDestroyed) return;
    const videoEvents = [
      { event: 'loadstart', handler: () => this.isLoading = true },
      { event: 'loadedmetadata', handler: () => this.onLoadedMetadataHandler() },
      { event: 'timeupdate', handler: () => this.onTimeUpdateHandler() },
      { event: 'play', handler: () => this.onVideoPlayHandler() },
      { event: 'pause', handler: () => this.onVideoPauseHandler() },
      { event: 'ended', handler: () => this.onVideoEndedHandler() },
      { event: 'volumechange', handler: () => this.onVolumeChangeHandler() },
      { event: 'waiting', handler: () => this.isWaiting = true },
      { event: 'canplay', handler: () => this.isWaiting = false },
      { event: 'enterpictureinpicture', handler: () => this.isPictureInPicture = true },
      { event: 'leavepictureinpicture', handler: () => this.isPictureInPicture = false },
      { event: 'error', handler: (e: any) => this.onVideoErrorHandler(e) }
    ];
    videoEvents.forEach(({ event, handler }) => {
      this.videoElement.addEventListener(event, handler);
      this.eventListeners.push(() => this.videoElement?.removeEventListener(event, handler));
    });
    if (this.player) {
      const errorHandler = (event: any) => {
        console.error('Shaka Player error:', event.detail);
        this.onError.emit(event.detail);
      };
      this.player.addEventListener('error', errorHandler);
      this.eventListeners.push(() => this.player?.removeEventListener('error', errorHandler));
    }
    const fullscreenEvents = [
      'fullscreenchange',
      'webkitfullscreenchange',
      'msfullscreenchange',
      'mozfullscreenchange'
    ];
    fullscreenEvents.forEach(event => {
      const handler = () => this.onFullscreenChangeHandler();
      document.addEventListener(event, handler);
      this.eventListeners.push(() => document.removeEventListener(event, handler));
    });
    this.addDocumentMouseHandlers();
  }

  private addDocumentMouseHandlers() {
    const mouseMoveHandler = (event: MouseEvent) => {
      if (this.isDraggingSeek) {
        this.onSeekBarMouseMove(event);
      }
    };
    const mouseUpHandler = () => {
      if (this.isDraggingSeek) {
        this.onDocumentMouseUp();
      }
    };
    document.addEventListener('mousemove', mouseMoveHandler);
    document.addEventListener('mouseup', mouseUpHandler);
    this.eventListeners.push(() => document.removeEventListener('mousemove', mouseMoveHandler));
    this.eventListeners.push(() => document.removeEventListener('mouseup', mouseUpHandler));
  }

  private onLoadedMetadataHandler() {
    this.duration = this.videoElement.duration;
    this.updateAvailableTracks();
    this.isLoading = false;
    this.cdr.detectChanges();
  }

  private onTimeUpdateHandler() {
    if (!this.isSeeking && !this.isDestroyed) {
      this.currentTime = this.videoElement.currentTime;
      this.onTimeUpdate.emit(this.currentTime); 
      this.updateBufferedTime();
      this.cdr.detectChanges();
    }
  }

  private onVideoPlayHandler() {
    this.isPlaying = true;
    this.isPaused = false;
    this.onPlay.emit();
    this.cdr.detectChanges();
  }

  private onVideoPauseHandler() {
    this.isPlaying = false;
    this.isPaused = true;
    this.onPause.emit();
    this.cdr.detectChanges();
  }

  private onVideoEndedHandler() {
    this.isPlaying = false;
    this.isPaused = true;
    this.onEnded.emit();
    this.cdr.detectChanges();
  }

  private onVolumeChangeHandler() {
    this.volume = this.videoElement.volume;
    this.isMuted = this.videoElement.muted;
    this.cdr.detectChanges();
  }

  private onVideoErrorHandler(error: any) {
    console.error('Video error:', error);
    this.onError.emit(error);
  }

  private async loadMedia() {
    if (!this.mediaUrl || !this.player || this.isDestroyed) return;
    try {
      this.isLoading = true;
      this.cdr.detectChanges();
      await this.player.load(this.mediaUrl);  
      this.scheduleTimeout(() => {
        this.updateAvailableTracks();
      }, 1000);
      if (this.startTime > 0) {
        this.videoElement.currentTime = this.startTime;
      }
      if (this.autoPlay) {
        await this.play();
      }
      this.isLoading = false;
      this.cdr.detectChanges();
    } catch (error) {
      console.error('❌ Media loading failed:', error);
      this.isLoading = false;
      this.onError.emit(error);
      this.cdr.detectChanges();
    }
  }

  private async loadNewMedia() {
    if (!this.mediaUrl || !this.player || this.isDestroyed) return;
    try {
      this.isLoading = true;
      this.currentTime = 0;
      this.isPlaying = false;
      this.isPaused = true;
      this.cdr.detectChanges();
      await this.player.load(this.mediaUrl);
      this.scheduleTimeout(() => {
        this.updateAvailableTracks();
      }, 1000);
      if (this.startTime > 0) {
        this.videoElement.currentTime = this.startTime;
      }
      this.isLoading = false;
      this.cdr.detectChanges();
      if (this.autoPlay) {
        await this.play();
      }
      this.onMediaUrlChange.emit(this.mediaUrl);
    } catch (error) {
      console.error('❌ New media loading failed:', error);
      this.isLoading = false;
      this.onError.emit(error);
      this.cdr.detectChanges();
    }
  }

  private updateAvailableTracks() {
    if (!this.player || this.isDestroyed) return;
    try {
      const tracks = this.player.getVariantTracks();
      this.availableQualities = tracks
        .filter((track: any) => track.videoCodec)
        .map((track: any) => ({
          id: track.id.toString(),
          height: track.height || 0,
          bandwidth: track.bandwidth || 0,
          label: `${track.height || 'Unknown'}p`
        }))
        .sort((a: any, b: any) => b.height - a.height);
      this.availableAudioTracks = tracks
        .filter((track: any) => track.audioCodec && track.language)
        .map((track: any) => ({
          id: track.id.toString(),
          language: track.language,
          label: track.label || track.language,
          type: 'audio' as const
        }));
      const textTracks = this.player.getTextTracks();
      console.log('Available text tracks:', textTracks);
      this.availableCaptions = textTracks.map((track: any) => ({
        id: track.id.toString(),
        language: track.language,
        label: track.label || track.language,
        type: 'text' as const
      }));
      console.log('Processed captions:', this.availableCaptions);

      // Restore caption preference
      try {
        const savedPreference = localStorage.getItem('caption-preference');
        if (savedPreference) {
          const preference = JSON.parse(savedPreference);
          if (preference.enabled && preference.language) {
            const savedTrack = this.availableCaptions.find((t: any) => t.language === preference.language);
            if (savedTrack) {
              this.selectCaption(savedTrack);
            }
          }
        }
      } catch (error) {
        console.warn('Failed to restore caption preference:', error);
      }

      this.cdr.detectChanges();
    } catch (error) {
      console.error('❌ Track update failed:', error);
    }
  }

  private updateBufferedTime() {
    if (this.videoElement && this.videoElement.buffered.length > 0) {
      this.bufferedTime = this.videoElement.buffered.end(0);
    }
  }

  private setupControlsAutoHide() {
    this.showControls();
  }

  private setupTVNavigation(): void {
    // Subscribe to focus changes for control button highlighting
    this.tvFocusSub = this.navService.focus$.subscribe(focusKey => {
      if (focusKey?.startsWith('player-controls:')) {
        const index = parseInt(focusKey.split(':')[1], 10);
        this.updateTVFocus(index);
      } else {
        this.updateTVFocus(-1);
      }
    });

    // Subscribe to player interactions for auto-hide timer reset
    this.tvInteractionSub = this.tvController.playerInteraction$.subscribe(() => {
      this.resetControlsTimerForTV();
    });
  }

  private updateTVFocus(index: number): void {
    // Remove focus from previous
    if (this.controlBtns && this.tvFocusedIndex >= 0) {
      const prevBtn = this.controlBtns.toArray()[this.tvFocusedIndex];
      if (prevBtn) {
        prevBtn.nativeElement.classList.remove('tv-focus');
      }
    }

    this.tvFocusedIndex = index;

    // Add focus to current
    if (this.controlBtns && index >= 0) {
      const btn = this.controlBtns.toArray()[index];
      if (btn) {
        btn.nativeElement.classList.add('tv-focus');
      }
    }

    this.cdr.detectChanges();
  }

  private resetControlsTimerForTV(): void {
    // Extend auto-hide timer when navigating controls (5s instead of 3s)
    this.clearControlsTimeout();
    this.controlsTimeout = this.scheduleTimeout(() => {
      if (this.isPlaying && !this.isDestroyed) {
        this.controlsVisible = false;
        this.hideAllPanels();
        this.tvController.exitTrapMode();
        // Suppress nav service — player handles keys directly when controls hidden
        this.navService.suppressNavigation();
        this.cdr.detectChanges();
      }
    }, 5000); // Extended timeout for TV navigation
  }

  private showControls() {
    this.controlsVisible = true;
    this.videoContainerRef.nativeElement.style.cursor = 'pointer';
    this.clearControlsTimeout();
    this.clearCursorTimeout();

    // TV: Enter focus trap mode — resume nav service so it handles D-pad in controls
    if (this.deviceService.isTV() && this.controlBtns) {
      this.navService.resumeNavigation();
      this.ngZone.runOutsideAngular(() => {
        setTimeout(() => {
          const buttons = this.controlBtns.toArray().map(q => q);
          if (buttons.length > 0 && this.controlsContainerRef) {
            this.tvController.enterTrapMode(
              'player-controls',
              this.controlsContainerRef,
              buttons,
              this.lastControlIndex
            );
          }
        }, 50);
      });
    }

    if (this.isNativeMobile && !this.isDestroyed) {
      this.scheduleTimeout(async () => {
        try {
          await NavigationBar.hide();
          await StatusBar.hide();
        } catch (error) {
          console.warn('Failed to re-hide navigation during controls show:', error);
        }
      }, 100);
    }

    // Use extended timeout for TV (5s), standard for others (3s)
    const timeout = this.deviceService.isTV() ? 5000 : 3000;
    this.controlsTimeout = this.scheduleTimeout(() => {
      if (this.isPlaying && !this.isDestroyed) {
        // Save focus position before hiding
        if (this.deviceService.isTV()) {
          this.lastControlIndex = this.navService.getFocusedIndex();
          this.tvController.exitTrapMode();
          // Suppress nav service — player handles keys directly when controls hidden
          this.navService.suppressNavigation();
        }
        this.controlsVisible = false;
        this.hideAllPanels();
        if (!this.isMobile) {
          this.videoContainerRef.nativeElement.style.cursor = 'none';
        }
        this.cdr.detectChanges();
      }
    }, timeout);
  }

  private clearCursorTimeout() {
    if (this.cursorTimeout) {
      this.clearScheduledTimeout(this.cursorTimeout);
      this.cursorTimeout = null;
    }
  }
  
  private clearControlsTimeout() {
    if (this.controlsTimeout) {
      this.clearScheduledTimeout(this.controlsTimeout);
      this.controlsTimeout = null;
    }
  }

  private scheduleTimeout(callback: () => void, delay: number): any {
    const timeoutId = setTimeout(callback, delay);
    this.timeouts.add(timeoutId);
    return timeoutId;
  }

  private clearScheduledTimeout(timeoutId: any) {
    clearTimeout(timeoutId);
    this.timeouts.delete(timeoutId);
  }

  private scheduleInterval(callback: () => void, delay: number): any {
    const intervalId = setInterval(callback, delay);
    this.intervals.add(intervalId);
    return intervalId;
  }

  private clearScheduledInterval(intervalId: any) {
    clearInterval(intervalId);
    this.intervals.delete(intervalId);
  }

  private scheduleAnimationFrame(callback: () => void): number {
    const frameId = requestAnimationFrame(callback);
    this.animationFrames.add(frameId);
    return frameId;
  }

  private clearScheduledAnimationFrame(frameId: number) {
    cancelAnimationFrame(frameId);
    this.animationFrames.delete(frameId);
  }

  private cleanup() {
    if (this.immersiveModeInterval) {
      this.clearScheduledInterval(this.immersiveModeInterval);
      this.immersiveModeInterval = null;
    }
    if (this.videoContainerRef?.nativeElement) {
      this.videoContainerRef.nativeElement.classList.remove('cursor-hidden');
    }
    this.cursorVisible = true;
    this.timeouts.forEach(id => clearTimeout(id));
    this.intervals.forEach(id => clearInterval(id));
    this.animationFrames.forEach(id => cancelAnimationFrame(id));
    this.timeouts.clear();
    this.intervals.clear();
    this.animationFrames.clear();
    this.eventListeners.forEach(removeListener => {
      try {
        removeListener();
      } catch (error) {
        console.warn('Error removing event listener:', error);
      }
    });
    this.eventListeners.length = 0;
    if (this.player) {
      this.player.destroy().catch((error: any) => {
        console.warn('Error destroying player:', error);
      });
      this.player = null;
    }
    if (this.isNativeMobile) {
      this.disableImmersiveMode().catch((error: any) => {
        console.warn('Error restoring native environment:', error);
      });
      ScreenOrientation.unlock().catch((error: any) => {
        console.warn('Error unlocking orientation:', error);
      });
    }
  }

  async play() {
    if (this.isDestroyed || !this.videoElement) return;
    try {
      await this.videoElement.play();
    } catch (error) {
      console.error('❌ Play failed:', error);
    }
  }

  togglePlayPause() {
    if (this.isPlaying) {
      this.pause();
      this.videoContainerRef.nativeElement.style.cursor = 'pointer';
    } else {
      this.play();
    }
  }
  
  pause() {
    if (this.isDestroyed || !this.videoElement) return;
    try {
      this.videoElement.pause();
      this.videoContainerRef.nativeElement.style.cursor = 'pointer';
    } catch (error) {
      console.error('❌ Pause failed:', error);
    }
  }

  seek(time: number) {
    if (this.isDestroyed || !this.videoElement) return;
    try {
      this.videoElement.currentTime = time;
      this.currentTime = time;
    } catch (error) {
      console.error('❌ Seek failed:', error);
    }
  }

  forward(seconds: number = 10) {
    const newTime = Math.min(this.currentTime + seconds, this.duration);
    this.seek(newTime);
  }

  backward(seconds: number = 10) {
    const newTime = Math.max(this.currentTime - seconds, 0);
    this.seek(newTime);
  }

  setVolume(volume: number) {
    if (this.isDestroyed || !this.videoElement) return;
    try {
      this.videoElement.volume = Math.max(0, Math.min(1, volume));
    } catch (error) {
      console.error('❌ Volume change failed:', error);
    }
  }

  toggleMute() {
    if (this.isDestroyed || !this.videoElement) return;
    try {
      this.videoElement.muted = !this.videoElement.muted;
    } catch (error) {
      console.error('❌ Mute toggle failed:', error);
    }
  }

  setPlaybackRate(rate: number) {
    if (this.isDestroyed || !this.videoElement) return;
    try {
      this.videoElement.playbackRate = rate;
      this.playbackRate = rate;
      this.hideAllPanels();
      this.cdr.detectChanges();
    } catch (error) {
      console.error('❌ Playback rate change failed:', error);
    }
  }

  toggleFullscreen() {
    if (this.isDestroyed) return;
    if (this.isMobileBrowser()) {
      if (!this.isFullscreen) {
        this.enterMobileBrowserFullscreen();
      } else {
        this.exitFullscreen();
      }
    } else if (!this.isMobile) {
      if (!this.isFullscreen) {
        this.enterFullscreen();
      } else {
        this.exitFullscreen();
      }
    }
  }

  private async enterMobileBrowserFullscreen() {
    if (this.isDestroyed) return;
    try {
      await this.enterFullscreen();
      await new Promise(resolve => setTimeout(resolve, 100));
      await this.aggressiveLandscapeLock();
    } catch (error) {
      console.warn('⚠️ Mobile browser fullscreen failed:', error);
    }
  }

  private async aggressiveLandscapeLock() {
    try {
      if (screen && (screen as any).orientation) {
        try {
          await (screen as any).orientation.lock('landscape-primary');
          return;
        } catch (e) {
          try {
            await (screen as any).orientation.lock('landscape');
            return;
          } catch (e2) {
            console.log('Modern API failed, trying legacy...');
          }
        }
      }
      const orientationMethods = [
        () => (screen as any).lockOrientation('landscape-primary'),
        () => (screen as any).lockOrientation('landscape'),
        () => (screen as any).webkitLockOrientation('landscape-primary'),
        () => (screen as any).webkitLockOrientation('landscape'),
        () => (screen as any).mozLockOrientation('landscape-primary'),
        () => (screen as any).mozLockOrientation('landscape'),
        () => (screen as any).msLockOrientation('landscape-primary'),
        () => (screen as any).msLockOrientation('landscape')
      ];
      for (const method of orientationMethods) {
        try {
          if (method()) {
            return;
          }
        } catch (e) {
        }
      }
      this.forceCSSLandscape();
    } catch (error) {
      console.warn('⚠️ All orientation methods failed:', error);
    }
  }
  
  private forceCSSLandscape() {
    try {
      const container = this.videoContainerRef.nativeElement;
      const isPortrait = window.innerHeight > window.innerWidth;
      if (isPortrait) {
        container.classList.add('force-landscape');
      }
    } catch (error) {
      console.warn('⚠️ CSS landscape failed:', error);
    }
  }

  private async enterFullscreen() {
    if (this.isDestroyed) return;
    try {
      const element = this.videoContainerRef.nativeElement;
      if (element.requestFullscreen) {
        await element.requestFullscreen();
      } else if ((element as any).webkitRequestFullscreen) {
        await (element as any).webkitRequestFullscreen();
      } else if ((element as any).msRequestFullscreen) {
        await (element as any).msRequestFullscreen();
      } else if ((element as any).mozRequestFullScreen) {
        await (element as any).mozRequestFullScreen();
      }
    } catch (error) {
      console.warn('❌ Fullscreen entry failed:', error);
    }
  }

  private async exitFullscreen() {
    if (this.isDestroyed) return;
    try {
      if (document.exitFullscreen) {
        await document.exitFullscreen();
      } else if ((document as any).webkitExitFullscreen) {
        await (document as any).webkitExitFullscreen();
      } else if ((document as any).msExitFullscreen) {
        await (document as any).msExitFullscreen();
      } else if ((document as any).mozCancelFullScreen) {
        await (document as any).mozCancelFullScreen();
      }
    } catch (error) {
      console.warn('❌ Fullscreen exit failed:', error);
    }
  }

  private onFullscreenChangeHandler() {
    if (this.isDestroyed) return;
    const isCurrentlyFullscreen = !!(document.fullscreenElement || (document as any).webkitFullscreenElement || (document as any).msFullscreenElement ||(document as any).mozFullScreenElement);
    this.isFullscreen = isCurrentlyFullscreen;
    if (this.isMobile && !this.isNativeMobile && !isCurrentlyFullscreen) {
      this.scheduleTimeout(() => {
        this.forceLandscapeMode();
      }, 100);
    }
    this.cdr.detectChanges();
  }

  async togglePictureInPicture() {
    if (this.isDestroyed || !this.videoElement) return;
    try {
      if (!this.isPictureInPicture) {
        if (this.videoElement.requestPictureInPicture) {
          await this.videoElement.requestPictureInPicture();
        }
      } else {
        if (document.exitPictureInPicture) {
          await document.exitPictureInPicture();
        }
      }
    } catch (error) {
      console.error('❌ Picture-in-Picture failed:', error);
    }
  }

  selectQuality(quality: QualityLevel) {
    if (!this.player || this.isDestroyed) return;
    try {
      if (quality.id === 'auto') {
        this.player.configure({ abr: { enabled: true } });
      } else {
        this.player.configure({ abr: { enabled: false } });
        const tracks = this.player.getVariantTracks();
        const selectedTrack = tracks.find((track: any) => track.id.toString() === quality.id);
        if (selectedTrack) {
          this.player.selectVariantTrack(selectedTrack);
        }
      }
      this.currentQuality = quality;
      this.hideAllPanels();
      this.cdr.detectChanges();
    } catch (error) {
      console.error('❌ Quality selection failed:', error);
    }
  }

  selectAudioTrack(track: VideoTrack) {
    if (!this.player || this.isDestroyed) return;
    try {
      const tracks = this.player.getVariantTracks();
      const selectedTrack = tracks.find((t: any) => t.id.toString() === track.id);
      if (selectedTrack) {
        this.player.selectVariantTrack(selectedTrack);
      }
      this.currentAudioTrack = track;
      this.hideAllPanels();
      this.cdr.detectChanges();
    } catch (error) {
      console.error('❌ Audio track selection failed:', error);
    }
  }

  selectCaption(track: VideoTrack | null) {
    if (!this.player || this.isDestroyed) return;
    try {
      if (track) {
        const textTracks = this.player.getTextTracks();
        const selectedTrack = textTracks.find((t: any) => t.id.toString() === track.id);
        if (selectedTrack) {
          this.player.selectTextTrack(selectedTrack);
          this.player.setTextTrackVisibility(true);
          // Save caption preference
          localStorage.setItem('caption-preference', JSON.stringify({
            language: track.language || '',
            label: track.label || '',
            enabled: true
          }));
        }
      } else {
        this.player.setTextTrackVisibility(false);
        // Save caption preference as disabled
        localStorage.setItem('caption-preference', JSON.stringify({
          language: '',
          label: '',
          enabled: false
        }));
      }
      this.currentCaption = track;
      this.hideAllPanels();
      this.cdr.detectChanges();
    } catch (error) {
      console.error('❌ Caption selection failed:', error);
    }
  }

  onSeekBarClick(event: MouseEvent) {
    if (this.isDestroyed || this.isDraggingSeek || (Date.now() - this.mouseDownTime) >= 200) return;
    const seekBar = this.seekBarRef.nativeElement;
    const rect = seekBar.getBoundingClientRect();
    const clickX = event.clientX - rect.left;
    const percentage = Math.max(0, Math.min(1, clickX / rect.width));
    const newTime = percentage * this.duration;
    this.seek(newTime);
    this.showSeekPreview = false;
    this.showControls();
  }

  onSeekBarMouseDown(event: MouseEvent) {
    if (this.isDestroyed) return;
    event.preventDefault();
    this.mouseDownTime = Date.now();
    this.isMouseDown = true;
    this.isSeeking = true;
    this.showSeekPreview = true;
    this.scheduleTimeout(() => {
      if (this.isMouseDown && !this.isDestroyed) {
        this.isDraggingSeek = true;
      }
    }, 100);
    this.onSeekBarMouseMove(event);
  }

  onSeekBarMouseMove(event: MouseEvent) {
    if (this.isDestroyed || !this.seekBarRef) return;
    const seekBar = this.seekBarRef.nativeElement;
    const rect = seekBar.getBoundingClientRect();
    const mouseX = Math.max(0, Math.min(rect.width, event.clientX - rect.left));
    const percentage = mouseX / rect.width;
    this.seekPreviewTime = percentage * this.duration;
    this.seekPreviewPosition = percentage * 100;
    if (!this.isDraggingSeek && !this.isSeeking) {
      this.showSeekPreview = true;
    }
    if (this.isDraggingSeek) {
      this.currentTime = this.seekPreviewTime;
      this.cdr.detectChanges();
    }
  }

  onSeekBarMouseEnter(event: MouseEvent) {
    if (this.isDestroyed) return;
    this.onSeekBarMouseMove(event);
  }

  onSeekBarMouseLeave() {
    if (this.isDestroyed || this.isDraggingSeek) return;
    this.showSeekPreview = false;
  }

  private onDocumentMouseUp() {
    if (this.isDestroyed) return;
    this.isMouseDown = false;
    if (this.isDraggingSeek) {
      this.seek(this.seekPreviewTime);
      this.isDraggingSeek = false;
    }
    this.isSeeking = false;
    this.showSeekPreview = false;
  }

  onSeekBarTouchStart(event: TouchEvent) {
    if (this.isDestroyed) return;
    event.preventDefault();
    const touch = event.touches[0];
    this.isDraggingSeek = true;
    this.isSeeking = true;
    this.showSeekPreview = true;
    const mockEvent = {
      clientX: touch.clientX,
      clientY: touch.clientY
    } as MouseEvent;
    this.onSeekBarMouseMove(mockEvent);
  }

  onSeekBarTouchMove(event: TouchEvent) {
    if (this.isDestroyed || !this.isDraggingSeek) return;
    event.preventDefault();
    const touch = event.touches[0];
    const mockEvent = {
      clientX: touch.clientX,
      clientY: touch.clientY
    } as MouseEvent;
    this.onSeekBarMouseMove(mockEvent);
  }

  onSeekBarTouchEnd(event: TouchEvent) {
    if (this.isDestroyed) return;
    event.preventDefault();
    if (this.isDraggingSeek) {
      this.seek(this.seekPreviewTime);
      this.isDraggingSeek = false;
      this.isSeeking = false;
      this.showSeekPreview = false;
    }
  }

  onVolumeInputChange(event: Event) {
    if (this.isDestroyed) return;
    const target = event.target as HTMLInputElement;
    this.setVolume(parseFloat(target.value));
  }

  toggleSettingsPanel() {
    this.showSettingsPanel = !this.showSettingsPanel;
    if (this.showSettingsPanel) {
      this.hideOtherPanels('settings');
    }
  }

  toggleQualityPanel() {
    this.showQualityPanel = !this.showQualityPanel;
    if (this.showQualityPanel) {
      this.hideOtherPanels('quality');
    }
  }

  toggleAudioPanel() {
    this.showAudioPanel = !this.showAudioPanel;
    if (this.showAudioPanel) {
      this.hideOtherPanels('audio');
    }
  }

  toggleCaptionsPanel() {
    this.showCaptionsPanel = !this.showCaptionsPanel;
    if (this.showCaptionsPanel) {
      this.hideOtherPanels('captions');
    }
  }

  toggleSpeedPanel() {
    this.showSpeedPanel = !this.showSpeedPanel;
    if (this.showSpeedPanel) {
      this.hideOtherPanels('speed');
    }
  }

  private hideOtherPanels(except: string) {
    if (except !== 'settings') this.showSettingsPanel = false;
    if (except !== 'quality') this.showQualityPanel = false;
    if (except !== 'audio') this.showAudioPanel = false;
    if (except !== 'captions') this.showCaptionsPanel = false;
    if (except !== 'speed') this.showSpeedPanel = false;
  }

  hideAllPanels() {
    this.showSettingsPanel = false;
    this.showQualityPanel = false;
    this.showAudioPanel = false;
    this.showCaptionsPanel = false;
    this.showSpeedPanel = false;
  }

  @HostListener('document:keydown', ['$event'])
  onKeyDown(event: KeyboardEvent) {
    if (this.isDestroyed) return;

    // On TV, handle D-pad differently
    if (this.deviceService.isTV()) {
      this.handleTVKeyDown(event);
      return;
    }

    const target = event.target as HTMLElement;
    if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable) {
      return;
    }
    switch (event.code) {
      case 'Space':
      case 'KeyK':
        event.preventDefault();
        this.togglePlayPause();
        this.showControlsFeedback('play-pause');
        break;
      case 'ArrowLeft':
      case 'KeyJ':
        event.preventDefault();
        const backwardTime = event.shiftKey ? 5 : 10;
        this.backward(backwardTime);
        this.showControlsFeedback(`-${backwardTime}s`);
        break;
      case 'ArrowRight':
      case 'KeyL':
        event.preventDefault();
        const forwardTime = event.shiftKey ? 5 : 10;
        this.forward(forwardTime);
        this.showControlsFeedback(`+${forwardTime}s`);
        break;
      case 'ArrowUp':
        event.preventDefault();
        const newVolumeUp = Math.min(1, this.volume + 0.1);
        this.setVolume(newVolumeUp);
        this.showControlsFeedback(`Volume ${Math.round(newVolumeUp * 100)}%`);
        break;
      case 'ArrowDown':
        event.preventDefault();
        const newVolumeDown = Math.max(0, this.volume - 0.1);
        this.setVolume(newVolumeDown);
        this.showControlsFeedback(`Volume ${Math.round(newVolumeDown * 100)}%`);
        break;
      case 'KeyM':
        event.preventDefault();
        this.toggleMute();
        this.showControlsFeedback(this.isMuted ? 'Muted' : 'Unmuted');
        break;
      case 'KeyF':
        event.preventDefault();
        this.toggleFullscreen();
        break;
      case 'KeyP':
        if (event.altKey) {
          event.preventDefault();
          this.togglePictureInPicture();
        }
        break;
      case 'Escape':
        if (this.showSettingsPanel || this.showQualityPanel || this.showAudioPanel || this.showCaptionsPanel || this.showSpeedPanel) {
          event.preventDefault();
          this.hideAllPanels();
        }
        break;
    }
    this.showControls();
  }

  private handleTVKeyDown(event: KeyboardEvent): void {
    const key = event.key;

    // Handle Back/Escape hierarchically
    if (key === 'Escape' || key === 'Backspace') {
      event.preventDefault();
      event.stopPropagation();
      this.handleTVBack();
      return;
    }

    // If controls are hidden, handle direct playback control
    if (!this.controlsVisible) {
      switch (key) {
        case 'Enter':
          // Center button toggles play/pause when controls hidden
          event.preventDefault();
          this.togglePlayPause();
          this.showControls();
          this.showControlsFeedback(this.isPlaying ? 'Play' : 'Pause');
          return;
        case 'ArrowLeft':
          // LEFT skips backward 10s
          event.preventDefault();
          this.backward(10);
          this.showControlsFeedback('-10s');
          this.showControls();
          return;
        case 'ArrowRight':
          // RIGHT skips forward 10s
          event.preventDefault();
          this.forward(10);
          this.showControlsFeedback('+10s');
          this.showControls();
          return;
        case 'ArrowUp':
        case 'ArrowDown':
          // Show controls on UP/DOWN (info overlay behavior)
          event.preventDefault();
          this.showControls();
          return;
      }
    }

    // When controls visible, let TVNavigationService handle arrow navigation
    // Enter activates focused button (handled by service)
  }

  private handleTVBack(): void {
    // Priority 1: Close settings menus
    if (this.showSettingsPanel || this.showQualityPanel ||
        this.showAudioPanel || this.showCaptionsPanel || this.showSpeedPanel) {
      this.hideAllPanels();
      return;
    }

    // Priority 2: Close episodes overlay
    if (this.showEpisodesOverlay) {
      this.showEpisodesOverlay = false;
      this.onEpisodesToggle.emit(false);
      // Re-enter controls trap
      if (this.controlsVisible && this.controlBtns) {
        setTimeout(() => {
          const buttons = this.controlBtns.toArray();
          if (buttons.length > 0 && this.controlsContainerRef) {
            this.tvController.enterTrapMode(
              'player-controls',
              this.controlsContainerRef,
              buttons,
              this.lastControlIndex
            );
          }
        }, 50);
      }
      return;
    }

    // Priority 3: Hide controls (if visible)
    if (this.controlsVisible) {
      this.lastControlIndex = this.navService.getFocusedIndex();
      this.tvController.exitTrapMode();
      this.navService.suppressNavigation();
      this.controlsVisible = false;
      this.hideAllPanels();
      this.cdr.detectChanges();
      return;
    }

    // Priority 4: Exit player (controls already hidden)
    this.navService.resumeNavigation();
    this.onBackClick.emit();
  }

  private showControlsFeedback(message: string) {
    if (this.isDestroyed) return;
    const feedback = document.createElement('div');
    feedback.className = 'keyboard-feedback';
    feedback.textContent = message;
    this.videoContainerRef.nativeElement.appendChild(feedback);
    this.scheduleTimeout(() => {
      if (feedback.parentNode) {
        feedback.parentNode.removeChild(feedback);
      }
    }, 1500);
  }

  @HostListener('dblclick', ['$event'])
  onDoubleClick(event: MouseEvent) {
    if (this.isDestroyed) return;
    if (this.isMobile) {
      return;
    }
    if ((event.target as HTMLElement).closest('.controls-container, .settings-panels')) {
      return;
    }
    this.toggleFullscreen();
  }

  @HostListener('click', ['$event'])
  onClick(event: MouseEvent) {
    if (this.isDestroyed) return;
    if ((event.target as HTMLElement).closest('.controls-container, .settings-panels, .center-play-btn, .progress-bar')) {
      return;
    }
    if (this.isMobile && this.isFullscreen) {
      return;
    }
    if (event.detail === 0 || this.isMobile) {
      return;
    }
    event.preventDefault();
    this.togglePlayPause();
  }

  @HostListener('touchstart', ['$event'])
  onTouchStart(event: TouchEvent) {
    if (this.isDestroyed || event.touches.length !== 1) return;
    const touch = event.touches[0];
    this.touchStartX = touch.clientX;
    this.touchStartY = touch.clientY;
    if ((event.target as HTMLElement).closest('.controls-container, .settings-panels, .center-play-btn, .progress-bar')) {
      return;
    }
    if (this.isMobile && this.isFullscreen) {
      const currentTime = Date.now();
      
      if (currentTime - this.lastTapTime < this.doubleTapThreshold) {
        this.tapCount++;
        if (this.tapCount === 2) {
          event.preventDefault();
          
          // Double-tap detected - check which side
          const screenWidth = window.innerWidth;
          const isLeftSide = touch.clientX < screenWidth / 2;
          
          if (isLeftSide) {
            this.backward(10);
            this.displaySkipIndicator('-10s', 'left');
          } else {
            this.forward(10);
            this.displaySkipIndicator('+10s', 'right');
          }
          
          this.tapCount = 0;
          return;
        }
      } else {
        this.tapCount = 1;
        this.scheduleTimeout(() => {
          if (this.tapCount === 1 && !this.isDestroyed) {
            this.showControls();
            this.tapCount = 0;
          }
        }, this.doubleTapThreshold);
      }
      
      this.lastTapTime = currentTime;
      return;
    }
    const currentTime = Date.now();
    if (currentTime - this.lastTapTime < 300) {
      this.tapCount++;
      if (this.tapCount === 2) {
        event.preventDefault();
        this.toggleFullscreen();
        this.tapCount = 0;
      }
    } else {
      this.tapCount = 1;
      this.scheduleTimeout(() => {
        if (this.tapCount === 1 && !this.isDestroyed) {
          this.showControls();
          this.tapCount = 0;
        }
      }, 300);
    }
    this.lastTapTime = currentTime;
  }
  
  private displaySkipIndicator(text: string, side: 'left' | 'right') {
    if (this.isDestroyed) return;
    
    this.skipIndicatorText = text;
    this.skipIndicatorSide = side;
    this.showSkipIndicator = true;
    this.cdr.detectChanges();
    
    this.scheduleTimeout(() => {
      if (!this.isDestroyed) {
        this.showSkipIndicator = false;
        this.cdr.detectChanges();
      }
    }, 1000);
  }

  @HostListener('touchend', ['$event'])
  onTouchEnd(event: TouchEvent) {
    if (this.isDestroyed) return;
    this.touchStartX = 0;
    this.touchStartY = 0;
  }

  @HostListener('touchmove', ['$event'])
  onTouchMove(event: TouchEvent) {
    if (this.isDestroyed || event.touches.length !== 1) return;
    const touch = event.touches[0];
    const deltaX = touch.clientX - this.touchStartX;
    const deltaY = touch.clientY - this.touchStartY;
    if (Math.abs(deltaX) > 50 && Math.abs(deltaY) < 50) {
      event.preventDefault();
      if (deltaX > 0) {
        this.forward(10);
        this.showControlsFeedback('+10s');
      } else {
        this.backward(10);
        this.showControlsFeedback('-10s');
      }
    }
    if (Math.abs(deltaY) > 50 && Math.abs(deltaX) < 50) {
      event.preventDefault();
      if (this.touchStartX > window.innerWidth / 2) {
        const volumeChange = -deltaY / 200;
        const newVolume = Math.max(0, Math.min(1, this.volume + volumeChange));
        this.setVolume(newVolume);
        this.showControlsFeedback(`Volume ${Math.round(newVolume * 100)}%`);
      }
    }
  }

  @HostListener('mousemove')
  @HostListener('touchstart')
  onUserInteraction() {
    if (this.isDestroyed) return;
    this.showControls();
    if (!this.isMobile && !this.controlsVisible) {
      this.videoContainerRef.nativeElement.style.cursor = 'pointer';
      this.clearCursorTimeout();
      this.cursorTimeout = this.scheduleTimeout(() => {
        if (!this.controlsVisible && this.isPlaying && !this.isDestroyed) {
          this.videoContainerRef.nativeElement.style.cursor = 'none';
        }
      }, 2000);
    }
  }

  @HostListener('window:focus')
  @HostListener('window:blur')
  onWindowFocusChange() {
    if (this.isNativeMobile && !this.isDestroyed) {
      this.scheduleTimeout(async () => {
        try {
          await this.enableImmersiveMode();
        } catch (error) {
          console.warn('⚠️ Failed to re-enable immersive mode on focus change:', error);
        }
      }, 500);
    }
  }

  @HostListener('window:orientationchange')
  onOrientationChange() {
    if (this.isDestroyed) return;
    this.scheduleTimeout(() => {
      if (this.isMobile && !this.isDestroyed) {
        this.lockLandscapeOrientation();
        if (this.isNativeMobile) {
          this.scheduleTimeout(async () => {
            try {
              await this.enableImmersiveMode();
            } catch (error) {
              console.warn('⚠️ Failed to re-enable immersive mode after orientation change:', error);
            }
          }, 1000);
        }
      }
    }, 500);
    this.cdr.detectChanges();
  }

  @HostListener('document:visibilitychange')
  onVisibilityChange() {
    if (this.isNativeMobile && !this.isDestroyed) {
      if (!document.hidden) {
        this.scheduleTimeout(async () => {
          try {
            await this.enableImmersiveMode();
          } catch (error) {
            console.warn('⚠️ Failed to re-enable immersive mode on visibility change:', error);
          }
        }, 300);
      }
    }
  }

  shouldShowFullscreenButton(): boolean {
    return this.isMobileBrowser() || !this.isMobile;
  }

  shouldShowEpisodesButton(): boolean {
    return this.hasEpisodes || this.hasMoreLikeThis;
  }

  toggleEpisodesOverlay() {
    this.showEpisodesOverlay = !this.showEpisodesOverlay;
    this.onEpisodesToggle.emit(this.showEpisodesOverlay);
    if (this.showEpisodesOverlay) {
      this.controlsVisible = false;
    }
  }

  closeEpisodesOverlay() {
    this.showEpisodesOverlay = false;
    this.onEpisodesToggle.emit(false);
    this.showControls();
  }

  onBackButtonClick() {
    if (this.isDestroyed) return;
    if (this.isNativeMobile) {
      this.disableImmersiveMode()
        .then(() => ScreenOrientation.unlock())
        .finally(() => {
          this.onBackClick.emit();
        });
    } else {
      this.onBackClick.emit();
    }
  }

  formatTime(seconds: number): string {
    if (isNaN(seconds) || seconds < 0) return '0:00';
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = Math.floor(seconds % 60);
    if (hours > 0) {
      return `${hours}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    }
    return `${minutes}:${secs.toString().padStart(2, '0')}`;
  }

  getProgressPercentage(): number {
    return this.duration > 0 ? (this.currentTime / this.duration) * 100 : 0;
  }

  getBufferedPercentage(): number {
    return this.duration > 0 ? (this.bufferedTime / this.duration) * 100 : 0;
  }

  getVolumePercentage(): number {
    return this.volume * 100;
  }

  private handleDoubleTap(touchX: number) {
    const screenWidth = window.innerWidth;
    const isLeftSide = touchX < screenWidth / 2;
    
    if (isLeftSide) {
      this.skipBackward(10);
      this.displaySkipIndicator('-10s', 'left');  // Shows "10s" overlay
    } else {
      this.skipForward(10);
      this.displaySkipIndicator('+10s', 'right'); // Shows "10s" overlay
    }
  }

  skipForward(seconds: number = 10) {
    if (this.duration > 0) {
      this.videoElement.currentTime = Math.min(this.duration, this.videoElement.currentTime + seconds);
    }
  }

  skipBackward(seconds: number = 10) {
    this.videoElement.currentTime = Math.max(0, this.videoElement.currentTime - seconds);
  }

  private cursorTimeout: any = null;
  cursorVisible:boolean = false;

  private showCursor() {
    this.cursorVisible = true;
    this.videoContainerRef.nativeElement.classList.remove('cursor-hidden');
  }
  
  private hideCursor() {
    if (!this.isMobile) { // Only hide cursor on desktop
      this.cursorVisible = false;
      this.videoContainerRef.nativeElement.classList.add('cursor-hidden');
    }
  }
  
  private resetCursorTimeout() {
    if (this.isPlaying && !this.controlsVisible && !this.isMobile) {
      // Show cursor briefly when mouse moves, then hide it again
      this.showCursor();
      this.scheduleTimeout(() => {
        if (!this.controlsVisible && this.isPlaying && !this.isDestroyed) {
          this.hideCursor();
        }
      }, 2000);
    }
  }
}